import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listMatches } from "@/lib/api";
import { ArrowRight, Zap, Activity, Target, Trophy, Eye } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function Home() {
  const [matches, setMatches] = useState([]);
  const { user } = useAuth();
  const canScore = user?.role === "admin" || user?.role === "scorer";

  useEffect(() => {
    listMatches().then(setMatches).catch(() => setMatches([]));
  }, []);

  const liveMatches = matches.filter((m) => m.status === "live").slice(0, 3);
  const recentMatches = matches.filter((m) => m.status === "completed").slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* SPECTATOR BANNER — visible to anonymous users */}
      {!user && (
        <Link
          to="/history"
          data-testid="home-spectator-link"
          className="block border-b border-primary bg-primary/5 hover:bg-primary/10 transition-colors"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3 group">
            <div className="flex items-center gap-3 min-w-0">
              <Eye className="w-5 h-5 text-primary shrink-0" strokeWidth={2.5} />
              <div className="min-w-0">
                <div className="font-display font-black uppercase text-sm tracking-tight text-primary">
                  I am a spectator — watch the score
                </div>
                <div className="text-[11px] text-muted-foreground truncate">
                  See live & past matches — no account needed
                </div>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-primary shrink-0 group-hover:translate-x-1 transition-transform" strokeWidth={3} />
          </div>
        </Link>
      )}

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/36741130/pexels-photo-36741130.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-transparent"></div>
        <div className="absolute inset-0 scanline opacity-30 pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-6">
              <span className="pulse-dot"></span>
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-destructive">
                Match Day
              </span>
            </div>
            <h1 className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter">
              Score every <span className="text-primary">delivery.</span>
              <br />
              Track every <span className="text-primary">legend.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              A tactical ball-by-ball scoring console for T20, ODI &amp; Test cricket. Run your gully, club or league match like a broadcast.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/new"
                data-testid="hero-start-match-btn"
                className="tactical-btn bg-primary text-primary-foreground hover:bg-[#C9E620] px-7 py-4 inline-flex items-center gap-3 text-sm"
              >
                Start a Match
                <ArrowRight className="w-4 h-4" strokeWidth={3} />
              </Link>
              <Link
                to="/history"
                data-testid="hero-history-btn"
                className="tactical-btn border border-border hover:border-primary text-foreground px-7 py-4 inline-flex items-center gap-3 text-sm"
              >
                View History
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-border bg-card p-6 relative">
              <div className="absolute -top-px -right-px w-12 h-12 border-t-2 border-r-2 border-primary"></div>
              <div className="absolute -bottom-px -left-px w-12 h-12 border-b-2 border-l-2 border-primary"></div>

              <div className="flex items-center justify-between mb-4">
                <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Live Console</span>
                <span className="text-xs font-mono text-primary">[ READY ]</span>
              </div>
              <div className="space-y-3 font-mono text-xs text-muted-foreground">
                <div className="flex justify-between border-b border-border pb-2">
                  <span>FORMAT</span>
                  <span className="text-foreground">T20 / ODI / TEST</span>
                </div>
                <div className="flex justify-between border-b border-border pb-2">
                  <span>BALL TYPES</span>
                  <span className="text-foreground">0/1/2/3/4/6/W/WD/NB/B/LB</span>
                </div>
                <div className="flex justify-between border-b border-border pb-2">
                  <span>UNDO</span>
                  <span className="text-primary">ENABLED</span>
                </div>
                <div className="flex justify-between">
                  <span>ANALYTICS</span>
                  <span className="text-primary">REAL-TIME</span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-2">
                {["0", "1", "4", "6", "WD", "W"].map((b) => (
                  <div
                    key={b}
                    className={`text-center py-3 font-display font-black text-lg border ${
                      b === "6"
                        ? "bg-primary text-primary-foreground border-primary"
                        : b === "W"
                        ? "bg-destructive text-destructive-foreground border-destructive"
                        : "border-border text-foreground"
                    }`}
                  >
                    {b}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES STRIP */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Zap, title: "One-Tap Scoring", desc: "Tactile keypad. Every legal & illegal ball." },
            { icon: Activity, title: "Live Stats", desc: "Run rate, required rate, partnerships." },
            { icon: Target, title: "Full Scorecard", desc: "Batting, bowling, extras, fall of wickets." },
            { icon: Trophy, title: "Career Tracker", desc: "Aggregate runs, wickets, averages." },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="border border-border p-5 hover:border-primary transition-colors">
              <Icon className="w-6 h-6 text-primary mb-3" strokeWidth={2} />
              <h3 className="font-display font-black uppercase text-lg tracking-tight">{title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* LIVE MATCHES */}
      {liveMatches.length > 0 && (
        <section className="border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <span className="pulse-dot"></span>
                <h2 className="font-display font-black uppercase text-3xl tracking-tight">Live Now</h2>
              </div>
              <Link to="/history" className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-primary">
                See all →
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {liveMatches.map((m) => (
                <MatchCard key={m.id} match={m} live canScore={canScore} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RECENT */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display font-black uppercase text-3xl tracking-tight">Recent Matches</h2>
            <Link to="/history" className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-primary">
              See all →
            </Link>
          </div>
          {recentMatches.length === 0 && liveMatches.length === 0 ? (
            <div className="border border-dashed border-border p-12 text-center">
              <p className="text-muted-foreground uppercase text-xs tracking-[0.2em] mb-4">No matches yet</p>
              <Link
                to="/new"
                data-testid="empty-start-match-btn"
                className="tactical-btn bg-primary text-primary-foreground px-6 py-3 inline-flex items-center gap-2 text-sm"
              >
                Start your first match
                <ArrowRight className="w-4 h-4" strokeWidth={3} />
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-4">
              {recentMatches.map((m) => (
                <MatchCard key={m.id} match={m} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function MatchCard({ match, live, canScore }) {
  const t1 = match.teams[0]?.name;
  const t2 = match.teams[1]?.name;
  const inn = match.innings_summary || [];
  const liveTo = canScore ? `/match/${match.id}/score` : `/watch/${match.id}`;
  return (
    <Link
      to={live ? liveTo : `/match/${match.id}`}
      data-testid={`match-card-${match.id}`}
      className="block border border-border p-5 hover:border-primary transition-colors group"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-bold">
          {match.format} · {match.overs} ov
        </span>
        {live ? (
          <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-destructive font-bold">
            <span className="pulse-dot"></span>Live
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-[0.3em] text-primary font-bold">Final</span>
        )}
      </div>
      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <span className="font-display uppercase text-xl font-black truncate pr-2">{t1}</span>
          <span className="font-mono text-base stat-tabular">
            {inn[0] ? `${inn[0].runs}/${inn[0].wickets}` : "—"}
          </span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="font-display uppercase text-xl font-black truncate pr-2">{t2}</span>
          <span className="font-mono text-base stat-tabular">
            {inn[1] ? `${inn[1].runs}/${inn[1].wickets}` : "—"}
          </span>
        </div>
      </div>
      {match.result && (
        <p className="mt-4 pt-3 border-t border-border text-xs text-primary font-bold">{match.result}</p>
      )}
    </Link>
  );
}
